/**
 * LiveCaptureButton — "Open Live Capture" button surfaced inside the
 * Agency Hub so an authenticated agent can jump straight into the
 * public kiosk (/agentevents) without re-entering their NPN.
 *
 * Requests a fresh kiosk session from POST /agent-events/hub-session
 * and then opens /agentevents in a new tab pre-loaded with the session
 * token and (optionally) the event pre-selected.
 *
 * Usage:
 *   <LiveCaptureButton event={{ event_id, name, date, kind }} />   // Event Detail
 *   <LiveCaptureButton />                                          // Events list — no pre-selected event
 */
import React, { useState } from 'react';
import api from '@/lib/api';
import { Sparkles, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

export default function LiveCaptureButton({
  event = null,
  label = 'Open Live Capture',
  className = '',
  size = 'md',
  variant = 'gold',
  testid = 'live-capture-btn',
}) {
  const [loading, setLoading] = useState(false);

  const launch = async () => {
    setLoading(true);
    try {
      const { data } = await api.post('/agent-events/hub-session');
      const params = new URLSearchParams({
        ref: data.agent_ref,
        session: data.session,
        npn: data.npn,
        agent_name: `${data.first_name} ${data.last_name || ''}`.trim(),
        can_sms: data.can_sms_leads ? '1' : '0',
      });
      if (event?.event_id) {
        params.set('event', event.event_id);
        if (event.name) params.set('event_name', event.name);
        if (event.date) params.set('event_date', event.date);
        if (event.kind) params.set('event_kind', event.kind);
      }
      window.open(`/agentevents?${params.toString()}`, '_blank', 'noopener,noreferrer');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not open Live Capture — only agents with an NPN can use the kiosk.');
    } finally { setLoading(false); }
  };

  const sizeCls = size === 'sm'
    ? 'px-3 py-1.5 text-xs'
    : size === 'lg' ? 'px-5 py-3 text-sm' : 'px-4 py-2 text-sm';
  const variantCls = variant === 'gold'
    ? 'bg-gradient-to-r from-[#f5c14f] to-[#e0a83a] text-[#0f2547] hover:from-[#f8ce6f] hover:to-[#e6b34e] shadow-sm ring-1 ring-[#f5c14f]/40'
    : variant === 'navy'
    ? 'bg-[#0f2547] text-white hover:bg-[#14335f]'
    : 'ring-1 ring-slate-200 text-slate-700 hover:bg-slate-50 bg-white';

  return (
    <button
      onClick={launch}
      disabled={loading}
      className={`${sizeCls} ${variantCls} rounded-lg font-semibold inline-flex items-center gap-1.5 transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${className}`}
      data-testid={testid}
      title="Opens the live-capture kiosk in a new tab"
    >
      {loading ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
      {label}
    </button>
  );
}
